/**
 * Authentication Composable
 *
 * Provides authentication state and operations throughout the application
 *
 * @example
 * import { useAuth } from '@/Composables/useAuth';
 *
 * const auth = useAuth();
 *
 * // Check authentication
 * if (auth.isAuthenticated.value) {
 *   console.log('User:', auth.fullName.value);
 * }
 *
 * // Check permissions
 * if (auth.checkPermission('users.create')) {
 *   // Show create button
 * }
 *
 * // Check roles
 * if (auth.hasRole('admin')) {
 *   // Show admin panel
 * }
 *
 * // Login
 * await auth.login({ email: 'user@example.com', password: 'password' });
 *
 * // Logout
 * await auth.logout();
 */

import { computed } from 'vue';
import { usePage, router } from '@inertiajs/vue3';
import { useToast } from '@/Composables/useToast';

/**
 * Main authentication composable
 */
export function useAuth() {
    const page = usePage();
    const toast = useToast();

    // =====================================================
    // STATE - Get from Inertia shared props
    // =====================================================

    /**
     * Current authenticated user
     * @type {ComputedRef<Object|null>}
     */
    const user = computed(() => page.props.auth?.user || null);

    /**
     * User's permissions array
     * @type {ComputedRef<Array<string>>}
     */
    const permissions = computed(() => page.props.auth?.permissions || []);

    /**
     * User's roles array
     * @type {ComputedRef<Array<string>>}
     */
    const roles = computed(() => page.props.auth?.roles || []);

    /**
     * Current company context
     * @type {ComputedRef<Object|null>}
     */
    const currentCompany = computed(() => page.props.auth?.company || null);

    /**
     * User's accessible companies
     * @type {ComputedRef<Array<Object>>}
     */
    const companies = computed(() => page.props.auth?.companies || []);

    // =====================================================
    // COMPUTED PROPERTIES
    // =====================================================

    /**
     * Check if user is authenticated
     * @type {ComputedRef<boolean>}
     */
    const isAuthenticated = computed(() => !!user.value);

    /**
     * Check if user's email is verified
     * @type {ComputedRef<boolean>}
     */
    const isEmailVerified = computed(() => {
        if (!user.value) return false;
        return user.value.email_verified_at !== null;
    });

    /**
     * Check if user has 2FA enabled
     * @type {ComputedRef<boolean>}
     */
    const has2FA = computed(() => {
        if (!user.value) return false;
        return user.value.two_fa_enabled || false;
    });

    /**
     * Get user's full name
     * @type {ComputedRef<string>}
     */
    const fullName = computed(() => {
        if (!user.value) return '';
        return `${user.value.first_name} ${user.value.last_name}`.trim();
    });

    /**
     * Get user's initials (for avatars)
     * @type {ComputedRef<string>}
     */
    const initials = computed(() => {
        if (!user.value) return '';
        const firstInitial = user.value.first_name?.charAt(0) || '';
        const lastInitial = user.value.last_name?.charAt(0) || '';
        return (firstInitial + lastInitial).toUpperCase();
    });

    /**
     * Check if user is active
     * @type {ComputedRef<boolean>}
     */
    const isActive = computed(() => {
        if (!user.value) return false;
        return user.value.is_active === true || user.value.is_active === 1;
    });

    /**
     * Check if user account is locked
     * @type {ComputedRef<boolean>}
     */
    const isLocked = computed(() => {
        if (!user.value) return false;
        if (!user.value.locked_until) return false;
        return new Date(user.value.locked_until) > new Date();
    });

    /**
     * Check if user has a company selected
     * @type {ComputedRef<boolean>}
     */
    const hasCompanySelected = computed(() => !!currentCompany.value);

    /**
     * Check if user has multiple companies
     * @type {ComputedRef<boolean>}
     */
    const hasMultipleCompanies = computed(() => companies.value.length > 1);

    /**
     * Get user's profile image URL
     * @type {ComputedRef<string|null>}
     */
    const profileImage = computed(() => {
        if (!user.value) return null;
        return user.value.profile_image_url || null;
    });

    /**
     * Get user's email
     * @type {ComputedRef<string>}
     */
    const email = computed(() => {
        if (!user.value) return '';
        return user.value.email || '';
    });

    // =====================================================
    // AUTHENTICATION METHODS
    // =====================================================

    /**
     * Login with credentials
     * @param {Object} credentials - Login credentials
     * @param {string} credentials.email - User email
     * @param {string} credentials.password - User password
     * @param {boolean} [credentials.remember] - Remember me
     * @param {Object} [options] - Inertia options
     * @returns {Promise}
     */
    const login = async (credentials, options = {}) => {
        return router.post('/api/auth/login', credentials, {
            preserveScroll: true,
            onSuccess: () => {
                if (!options.silent) {
                    toast.success('Welcome back!', 'Login Successful');
                }
            },
            onError: (errors) => {
                if (errors.email) {
                    toast.error(errors.email, 'Login Failed');
                } else if (errors.password) {
                    toast.error(errors.password, 'Login Failed');
                } else if (errors.message) {
                    toast.error(errors.message, 'Login Failed');
                }
            },
            ...options,
        });
    };

    /**
     * Logout current user
     * @param {Object} [options] - Inertia options
     * @returns {Promise}
     */
    const logout = async (options = {}) => {
        return router.post('/api/auth/logout', {}, {
            preserveScroll: false,
            onSuccess: () => {
                if (!options.silent) {
                    toast.info('You have been logged out', 'Goodbye');
                }
            },
            onError: (errors) => {
                toast.error('Failed to logout', 'Error');
            },
            ...options,
        });
    };

    /**
     * Register new user
     * @param {Object} data - Registration data
     * @param {Object} data.company - Company information
     * @param {Object} data.user - User information
     * @param {number} [data.plan_id] - Subscription plan ID
     * @param {Object} [options] - Inertia options
     * @returns {Promise}
     */
    const register = async (data, options = {}) => {
        return router.post('/api/auth/register', data, {
            preserveScroll: true,
            onSuccess: () => {
                if (!options.silent) {
                    toast.success('Account created successfully! Please verify your email.', 'Welcome');
                }
            },
            onError: (errors) => {
                // Errors will be handled by the form component
                if (errors.message) {
                    toast.error(errors.message, 'Registration Failed');
                }
            },
            ...options,
        });
    };

    /**
     * Update user profile
     * @param {Object} data - Profile data to update
     * @param {Object} [options] - Inertia options
     * @returns {Promise}
     */
    const updateProfile = async (data, options = {}) => {
        return router.put('/api/profile/update', data, {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => {
                if (!options.silent) {
                    toast.success('Profile updated successfully', 'Success');
                }
            },
            onError: (errors) => {
                if (errors.message) {
                    toast.error(errors.message, 'Update Failed');
                }
            },
            ...options,
        });
    };

    /**
     * Update user password
     * @param {Object} data - Password data
     * @param {string} data.current_password - Current password
     * @param {string} data.password - New password
     * @param {string} data.password_confirmation - Password confirmation
     * @param {Object} [options] - Inertia options
     * @returns {Promise}
     */
    const updatePassword = async (data, options = {}) => {
        return router.put('/api/profile/password', data, {
            preserveScroll: true,
            onSuccess: () => {
                if (!options.silent) {
                    toast.success('Password updated successfully', 'Success');
                }
            },
            onError: (errors) => {
                if (errors.current_password) {
                    toast.error(errors.current_password, 'Update Failed');
                } else if (errors.message) {
                    toast.error(errors.message, 'Update Failed');
                }
            },
            ...options,
        });
    };

    /**
     * Request email verification
     * @param {Object} [options] - Inertia options
     * @returns {Promise}
     */
    const resendVerification = async (options = {}) => {
        return router.post('/api/auth/resend-verification', {}, {
            preserveScroll: true,
            onSuccess: () => {
                if (!options.silent) {
                    toast.success('Verification email sent! Please check your inbox.', 'Email Sent');
                }
            },
            onError: (errors) => {
                if (errors.message) {
                    toast.error(errors.message, 'Error');
                }
            },
            ...options,
        });
    };

    // =====================================================
    // PERMISSION & ROLE METHODS
    // =====================================================

    /**
     * Check if user has a specific permission
     * @param {string} permission - Permission name (e.g., 'users.create')
     * @returns {boolean}
     */
    const checkPermission = (permission) => {
        if (!isAuthenticated.value) return false;
        return permissions.value.includes(permission);
    };

    /**
     * Check if user has any of the provided permissions
     * @param {Array<string>} permissionArray - Array of permission names
     * @returns {boolean}
     */
    const hasAnyPermission = (permissionArray) => {
        if (!isAuthenticated.value) return false;
        return permissionArray.some(permission => checkPermission(permission));
    };

    /**
     * Check if user has all of the provided permissions
     * @param {Array<string>} permissionArray - Array of permission names
     * @returns {boolean}
     */
    const hasAllPermissions = (permissionArray) => {
        if (!isAuthenticated.value) return false;
        return permissionArray.every(permission => checkPermission(permission));
    };

    /**
     * Check if user has a specific role
     * @param {string} role - Role name (e.g., 'admin')
     * @returns {boolean}
     */
    const hasRole = (role) => {
        if (!isAuthenticated.value) return false;
        return roles.value.includes(role);
    };

    /**
     * Check if user has any of the provided roles
     * @param {Array<string>} roleArray - Array of role names
     * @returns {boolean}
     */
    const hasAnyRole = (roleArray) => {
        if (!isAuthenticated.value) return false;
        return roleArray.some(role => hasRole(role));
    };

    /**
     * Check if user has all of the provided roles
     * @param {Array<string>} roleArray - Array of role names
     * @returns {boolean}
     */
    const hasAllRoles = (roleArray) => {
        if (!isAuthenticated.value) return false;
        return roleArray.every(role => hasRole(role));
    };

    /**
     * Check if user is a super admin
     * @returns {boolean}
     */
    const isSuperAdmin = computed(() => {
        return hasRole('Super Admin') || hasRole('super_admin');
    });

    /**
     * Check if user is an admin
     * @returns {boolean}
     */
    const isAdmin = computed(() => {
        return hasRole('Admin') || hasRole('admin') || isSuperAdmin.value;
    });

    // =====================================================
    // COMPANY METHODS
    // =====================================================

    /**
     * Switch to a different company
     * @param {number} companyId - Company ID to switch to
     * @param {Object} [options] - Inertia options
     * @returns {Promise}
     */
    const switchCompany = async (companyId, options = {}) => {
        return router.post(`/api/auth/companies/${companyId}/switch`, {}, {
            preserveScroll: false,
            onSuccess: () => {
                if (!options.silent) {
                    const company = companies.value.find(c => c.id === companyId);
                    const companyName = company?.name || 'company';
                    toast.success(`Switched to ${companyName}`, 'Company Changed');
                }
            },
            onError: (errors) => {
                if (errors.message) {
                    toast.error(errors.message, 'Switch Failed');
                }
            },
            ...options,
        });
    };

    /**
     * Get current company's subscription status
     * @returns {Object|null}
     */
    const subscription = computed(() => {
        if (!currentCompany.value) return null;
        return currentCompany.value.subscription || null;
    });

    /**
     * Check if current company has an active subscription
     * @returns {boolean}
     */
    const hasActiveSubscription = computed(() => {
        if (!subscription.value) return false;
        return subscription.value.status === 'active';
    });

    /**
     * Check if current company is on trial
     * @returns {boolean}
     */
    const isOnTrial = computed(() => {
        if (!subscription.value) return false;
        return subscription.value.is_trialing || false;
    });

    // =====================================================
    // 2FA METHODS
    // =====================================================

    /**
     * Enable 2FA for user
     * @param {Object} data - 2FA data
     * @param {string} data.method - 2FA method ('email' or 'authenticator')
     * @param {Object} [options] - Inertia options
     * @returns {Promise}
     */
    const enable2FA = async (data, options = {}) => {
        return router.post('/api/auth/2fa/enable', data, {
            preserveScroll: true,
            onSuccess: () => {
                if (!options.silent) {
                    toast.success('Two-factor authentication enabled', 'Security Enhanced');
                }
            },
            onError: (errors) => {
                if (errors.message) {
                    toast.error(errors.message, 'Error');
                }
            },
            ...options,
        });
    };

    /**
     * Disable 2FA for user
     * @param {Object} data - Confirmation data
     * @param {string} data.password - User password
     * @param {Object} [options] - Inertia options
     * @returns {Promise}
     */
    const disable2FA = async (data, options = {}) => {
        return router.post('/api/auth/2fa/disable', data, {
            preserveScroll: true,
            onSuccess: () => {
                if (!options.silent) {
                    toast.info('Two-factor authentication disabled', 'Security Updated');
                }
            },
            onError: (errors) => {
                if (errors.message) {
                    toast.error(errors.message, 'Error');
                }
            },
            ...options,
        });
    };

    // =====================================================
    // UTILITY METHODS
    // =====================================================

    /**
     * Refresh user data
     * @returns {Promise}
     */
    const refresh = async () => {
        return router.reload({
            only: ['auth'],
            preserveScroll: true,
            preserveState: true,
        });
    };

    /**
     * Get user property safely
     * @param {string} key - Property key
     * @param {any} defaultValue - Default value if not found
     * @returns {any}
     */
    const getUserProperty = (key, defaultValue = null) => {
        if (!user.value) return defaultValue;
        return user.value[key] ?? defaultValue;
    };

    /**
     * Check if user can access a route
     * @param {string} routeName - Route name
     * @returns {boolean}
     */
    const canAccessRoute = (routeName) => {
        // This would need route permissions mapping
        // For now, just check if authenticated
        return isAuthenticated.value;
    };

    // =====================================================
    // RETURN PUBLIC API
    // =====================================================

    return {
        // Core State
        user,
        permissions,
        roles,
        currentCompany,
        companies,

        // Computed Properties
        isAuthenticated,
        isEmailVerified,
        has2FA,
        fullName,
        initials,
        isActive,
        isLocked,
        hasCompanySelected,
        hasMultipleCompanies,
        profileImage,
        email,
        isSuperAdmin,
        isAdmin,

        // Subscription
        subscription,
        hasActiveSubscription,
        isOnTrial,

        // Authentication Methods
        login,
        logout,
        register,
        updateProfile,
        updatePassword,
        resendVerification,

        // Permission Methods
        checkPermission,
        hasAnyPermission,
        hasAllPermissions,

        // Role Methods
        hasRole,
        hasAnyRole,
        hasAllRoles,

        // Company Methods
        switchCompany,

        // 2FA Methods
        enable2FA,
        disable2FA,

        // Utility Methods
        refresh,
        getUserProperty,
        canAccessRoute,
    };
}

/**
 * Permission directive helper
 * Use in template: v-if="$can('users.create')"
 */
export function can(permission) {
    const { checkPermission } = useAuth();
    return checkPermission(permission);
}

/**
 * Role directive helper
 * Use in template: v-if="$hasRole('admin')"
 */
export function hasRoleHelper(role) {
    const { hasRole } = useAuth();
    return hasRole(role);
}
